// App.tsx
import React from 'react';
import './styles/globals/App.css';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import Layout from './components/Layout';
import { ToastProvider } from './components/Toast/ToastProvider';
import { ErrorBoundary } from './components/ErrorBoundary';
import HomePage from './pages/HomePage';
// import DepositPage from './pages/DepositPage'; // Temporarily disabled
// import ModelManagerPage from './pages/ModelManagerPage'; // Temporarily disabled
// import WorkerManagerPage from './pages/WorkerManagerPage'; // Temporarily disabled
import ModelsGalleryPageWrapper from './pages/ModelsGalleryPage';
import ChatPage from './pages/ChatPage';
import TextChatPage from './pages/TextChatPage';
import ImageGenerationPage from './pages/ImageGenerationPage';
import ProfilePage from './pages/ProfilePage';
import SupplyGpuPage from './pages/SupplyGpuPage';
import HistoryPage from './pages/HistoryPage';
import AuthPage from './pages/AuthPage';
import GenericComingSoonPage from './pages/GenericComingSoonPage';
import HelpPage from './pages/HelpPage';
import ToolsPage from './pages/ToolsPage';
import SummarizerAgentPage from './pages/SummarizerAgentPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetEmailSentPage from './pages/ResetEmailSentPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PasswordChangedPage from './pages/PasswordChangedPage';
import RegisterPage from './pages/RegisterPage';
import RegisterActivatePage from './pages/RegisterActivatePage';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthWrapper } from './contexts/AuthWrapper';
import { ThemeProvider } from './contexts/ThemeContext';
import { WalletProvider } from './contexts/WalletContext';
import { queryClient } from './services/queryClient';
import { HelmetProvider } from 'react-helmet-async';
import LandingPage from './pages/LandingPage';
import UnifiedApiLandingPage from './pages/UnifiedApiLandingPage';
import GpuRentalLandingPage from './pages/GpuRentalLandingPage';
import FreeInfrastructureLandingPage from './pages/FreeInfrastructureLandingPage';
import { useAuth } from '@/contexts/AuthWrapper';
import LogoLoader from './components/LogoLoader';

const AnimatedRoutes: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();

  const isAuthPage = [
    '/login',
    '/auth',
    '/forgot-password',
    '/forgot-password/sent',
    '/reset-password',
    '/password-changed',
    '/register',
    '/register/activate',
  ].includes(location.pathname);

  // that doesn't redirect logged-in users away.
  const isLandingRoute =
    location.pathname === '/' ||
    location.pathname === '/landing' ||
    location.pathname === '/unified-api' ||
    location.pathname === '/gpu-rental' ||
    location.pathname === '/free-infrastructure' ||
    location.pathname === '/for-developers';

  if (isLandingRoute) {
    // Don't show the Swarmind logo loader on the public landing routes.
    // If the user is authenticated, redirect once auth is resolved.
    if (!isLoading && location.pathname === '/' && isAuthenticated) {
      return <Navigate to="/text-chat" replace />;
    }
    return (
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/unified-api" element={<UnifiedApiLandingPage />} />
          <Route path="/gpu-rental" element={<GpuRentalLandingPage />} />
          <Route path="/for-developers" element={<FreeInfrastructureLandingPage />} />
          <Route path="/free-infrastructure" element={<Navigate to="/for-developers" replace />} />
        </Routes>
      </AnimatePresence>
    );
  }

  if (isAuthPage) {
    return (
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route
            path="/forgot-password/sent"
            element={<ResetEmailSentPage />}
          />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/password-changed" element={<PasswordChangedPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/register/activate" element={<RegisterActivatePage />} />
          <Route path="/signup" element={<Navigate to="/register" replace />} />
          <Route path="/" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </AnimatePresence>
    );
  }

  return (
    <ProtectedRoute>
      <Layout>
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            {/* <Route path="/deposit" element={<DepositPage />} /> */}{' '}
            {/* Temporarily disabled */}
            <Route path="/models" element={<ModelsGalleryPageWrapper />} />
            <Route path="/chat" element={<ChatPage />} />
            <Route path="/text-chat" element={<TextChatPage />} />
            <Route path="/image-generation" element={<ImageGenerationPage />} />
            {/* <Route path="/workers" element={<WorkerManagerPage />} /> */}{' '}
            {/* Temporarily disabled */}
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/profile/wallet" element={<ProfilePage />} />
            <Route path="/profile/history" element={<ProfilePage />} />
            <Route path="/supply-gpu" element={<SupplyGpuPage />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/" element={<HomePage />} />
            {/* Help & Coming Soon Pages */}
            <Route path="/help" element={<HelpPage />} />
            <Route path="/pricing" element={<GenericComingSoonPage />} />
            <Route path="/tools" element={<ToolsPage />} />
            <Route path="/tools/summarizer" element={<SummarizerAgentPage />} />
            <Route path="/tts" element={<GenericComingSoonPage />} />
            {/* Catch-all route for undefined pages - shows coming soon page */}
            <Route path="*" element={<GenericComingSoonPage />} />
          </Routes>
        </AnimatePresence>
      </Layout>
    </ProtectedRoute>
  );
};

const App: React.FC = () => {
  // Apply saved theme on initial load (default is dark)
  React.useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'light') {
      document.documentElement.removeAttribute('data-theme');
    } else {
      document.documentElement.setAttribute('data-theme', 'dark');
    }
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <AuthWrapper>
              <WalletProvider>
                <ThemeProvider>
                  <Router>
                    <AnimatedRoutes />
                  </Router>
                </ThemeProvider>
              </WalletProvider>
            </AuthWrapper>
          </ToastProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
